import { isUsableCoord } from "../../utils/map-view";
import { EditableFence } from "./types";
import { formatArea, normaliseCoordinates } from "./mapUtils";

/**
 * Largest fence we accept. A venue fence traces a building or car park; anything
 * approaching a whole suburb is almost certainly a mis-drag.
 */
export const MAX_FENCE_AREA_M2 = 5_000_000; // 5 km²

export const MIN_FENCE_POINTS = 3;

/**
 * Returns a user-facing problem with the fence, or null when it can be saved.
 * `others` is every other fence, used for the duplicate-name check.
 */
export const validateFence = (
  fence: EditableFence,
  others: EditableFence[],
  areaM2: number | null
): string | null => {
  const name = fence.name.trim();

  if (!name) {
    return "Please give the fence a name before saving.";
  }

  const clash = others.find(
    (f) =>
      f.locationId !== fence.locationId &&
      f.name.trim().toLowerCase() === name.toLowerCase()
  );
  if (clash) {
    return `A fence named "${clash.name}" already exists. Choose a different name.`;
  }

  const points = normaliseCoordinates(fence.coordinates).filter(isUsableCoord);
  if (points.length < MIN_FENCE_POINTS) {
    return `A fence needs at least ${MIN_FENCE_POINTS} distinct points. This one has ${points.length}.`;
  }

  if (areaM2 !== null && areaM2 > MAX_FENCE_AREA_M2) {
    return `This fence covers ${formatArea(areaM2)}, which is larger than the ${formatArea(
      MAX_FENCE_AREA_M2
    )} limit. Drag the corners in before saving.`;
  }

  return null;
};
